import "./topNav.css";

import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Drawer, IconButton } from "@mui/material";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";

import { NAV_LINKS, SECTIONS, PAGE_ROUTES, CONTACT_DETAILS } from "../../utils/stateTypes";
import { scrollToSection } from "../../utils/generalUtils";
import { useScrolledPast, useActiveSection } from "../general/hooks";

const SECTION_IDS = NAV_LINKS.map((link) => link.section);

//Nav link to a section of the home page - smooth scrolls when already on the home page
//(onClick is pulled out of props so wrappers like MUI's Button, which always pass onClick, can't overwrite the scroll handler)
export const SectionLink = React.forwardRef(({ section, className = "", onNavigate = null, onClick: onClickProp, children, ...props }, ref) => {
  const { pathname } = useLocation();

  const onClick = (e) => {
    if (onClickProp) {
      onClickProp(e);
    }
    if (pathname === PAGE_ROUTES.HOME && scrollToSection(section)) {
      e.preventDefault();
    }
    if (onNavigate) {
      onNavigate();
    }
  };

  return (
    <Link
      ref={ref}
      to={`${PAGE_ROUTES.HOME}#${section}`}
      className={className}
      onClick={onClick}
      {...props}
    >
      {children}
    </Link>
  );
});

const TopNav = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const isScrolled = useScrolledPast(40);
  const activeSection = useActiveSection(SECTION_IDS);
  const { pathname } = useLocation();

  const closeMenu = () => setIsMenuOpen(false);

  const onLogoClick = (e) => {
    if (pathname === PAGE_ROUTES.HOME) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      window.history.replaceState(null, "", PAGE_ROUTES.HOME);
    }
  };

  return (
    <header className={`top-nav ${isScrolled ? "is-scrolled" : ""}`}>
      <div className={"container"}>
        <nav
          className={"top-nav-bar"}
          aria-label={"Main"}
        >
          <Link
            to={PAGE_ROUTES.HOME}
            className={"top-nav-logo"}
            onClick={onLogoClick}
          >
            <img
              src={"/images/logo.png"}
              alt={"Corlea Geyer Attorneys Conveyancers - home"}
              width={780}
              height={258}
            />
          </Link>

          <div className={"top-nav-links"}>
            {NAV_LINKS.map((link) => (
              <SectionLink
                key={link.section}
                section={link.section}
                className={`top-nav-link ${activeSection === link.section ? "is-active" : ""}`}
                aria-current={activeSection === link.section ? "true" : undefined}
              >
                {link.label}
              </SectionLink>
            ))}
            <Button
              component={SectionLink}
              section={SECTIONS.CONTACT}
              variant={"contained"}
              className={"top-nav-cta"}
            >
              Contact Us
            </Button>
          </div>

          <IconButton
            className={"top-nav-menu-btn"}
            aria-label={"Open menu"}
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        </nav>
      </div>

      <Drawer
        anchor={"right"}
        open={isMenuOpen}
        onClose={closeMenu}
        PaperProps={{ className: "mobile-menu" }}
      >
        <IconButton
          className={"mobile-menu-close"}
          aria-label={"Close menu"}
          onClick={closeMenu}
        >
          <CloseIcon />
        </IconButton>
        <nav
          className={"mobile-menu-links"}
          aria-label={"Mobile"}
        >
          {NAV_LINKS.map((link) => (
            <SectionLink
              key={link.section}
              section={link.section}
              onNavigate={closeMenu}
            >
              {link.label}
            </SectionLink>
          ))}
          <SectionLink
            section={SECTIONS.CONTACT}
            onNavigate={closeMenu}
          >
            Contact Us
          </SectionLink>
        </nav>
        <div className={"mobile-menu-contact"}>
          <a href={CONTACT_DETAILS.PHONE_LINK}>{CONTACT_DETAILS.PHONE}</a>
          <a href={`mailto:${CONTACT_DETAILS.EMAIL}`}>{CONTACT_DETAILS.EMAIL}</a>
        </div>
      </Drawer>
    </header>
  );
};

export default TopNav;
